import { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "blackride-theme";
const THEME_VERSION_KEY = "blackride-theme-version";
const CURRENT_THEME_VERSION = "light-default-v1";

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") {
      return "light";
    }

    const version = localStorage.getItem(THEME_VERSION_KEY);
    const storedTheme = localStorage.getItem(STORAGE_KEY);

    if (version !== CURRENT_THEME_VERSION) {
      localStorage.setItem(STORAGE_KEY, "light");
      localStorage.setItem(THEME_VERSION_KEY, CURRENT_THEME_VERSION);
      return "light";
    }

    return storedTheme || "light";
  });

  useEffect(() => {
    document.body.dataset.theme = theme;
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(STORAGE_KEY, theme);
    localStorage.setItem(THEME_VERSION_KEY, CURRENT_THEME_VERSION);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === "dark",
      isLight: theme === "light",
      setTheme,
      toggleTheme: () => setTheme((current) => (current === "dark" ? "light" : "dark")),
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }

  return context;
}
